import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { fetchJson } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/data/status-badge";
import { Download } from "lucide-react";
import type { CargaPublicaDTO } from "@shared/mercado-comprador";

/**
 * `/c/:token` — a CARGA que o distribuidor abre. FORA do gate de autenticação
 * (ver App.tsx): quem abre não tem conta no Rizoma, e é justamente por isso que o
 * link existe.
 *
 * O terceiro lado da mesa: o produtor abre `/v/`, o comprador formou a carga, e
 * aqui está quem RECEBE. Mobile-first e sem menu, como o `/v/`: o uso real é o
 * distribuidor no galpão conferindo procedência e peso no celular.
 *
 * ⚠️ O QUE ESTA TELA NÃO PODE MOSTRAR, e o servidor por isso não manda: VALOR
 * NENHUM (decisão D4 dos Cargas v2). Nem o preço pago a cada produtor, nem preço
 * de venda, nem total — a carga, nesta fase, é o controle do corretor, e o que o
 * cliente confere na chegada é procedência e classificação, em kg. O DTO
 * (`CHAVES_DTO_CARGA_PUBLICA`) é a fronteira, e tem teste de contrato.
 *
 * ⚠️ O NOME DO PRODUTOR já chega RESOLVIDO (D5): quando o corretor desligou
 * "nome dos produtores", o servidor manda "Produtor 1", "Produtor 2"… Esconder
 * na tela deixaria o nome verdadeiro viajando no JSON de um link público.
 */

function fmtKg(v: number) {
  return `${v.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} kg`;
}
function fmtData(iso: string) {
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}

export default function CargaPublicaPage() {
  const { token } = useParams<{ token: string }>();
  const [, setLocation] = useLocation();

  const { data, isLoading, isError } = useQuery<CargaPublicaDTO>({
    queryKey: ["/api/publico/carga", token],
    queryFn: () => fetchJson<CargaPublicaDTO>(`/api/publico/carga/${token}`),
    enabled: !!token,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-sm text-muted-foreground">
        Abrindo a carga…
      </div>
    );
  }

  // Expirado, inexistente e carga ainda em formação chegam como o MESMO 404 — a
  // tela não adivinha qual dos três foi, porque o servidor de propósito não conta.
  if (isError || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-2 p-6 text-center">
        <p className="font-bold">Este link não está mais disponível.</p>
        <p className="text-sm text-muted-foreground max-w-xs">
          Peça um link novo para quem enviou a carga.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* 480px: o documento é estreito de propósito, como o romaneio — ninguém
          imprime isto, abre-se no celular. */}
      <div className="mx-auto w-full max-w-[480px] bg-background min-h-screen">
        <header className="bg-[#111827] text-white px-4 py-5 space-y-1">
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-lg font-bold font-mono">{data.identificador}</h1>
            <StatusBadge tom={data.status === "ENTREGUE" ? "sucesso" : "info"}>
              {data.status === "ENTREGUE" ? "Entregue" : "Fechada"}
            </StatusBadge>
          </div>
          <p className="text-xs text-white/70">
            {[data.comprador.nome, data.comprador.cidade, data.comprador.telefone].filter(Boolean).join(" · ")}
          </p>
        </header>

        <div className="p-4 space-y-5">
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Carregamento</dt>
              <dd>{data.dataCarregamento ? fmtData(data.dataCarregamento) : "—"}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Cliente</dt>
              <dd className="truncate">{data.cliente?.nome ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Destino</dt>
              <dd className="truncate">{data.cliente?.local ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Produtores</dt>
              <dd>{data.produtores.length} · {data.nRomaneios} {data.nRomaneios === 1 ? "romaneio" : "romaneios"}</dd>
            </div>
            {(data.placa || data.motoristaNome) && (
              <>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">Placa</dt>
                  <dd className="font-mono">{data.placa ?? "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">Motorista</dt>
                  <dd className="truncate">{data.motoristaNome ?? "—"}</dd>
                </div>
              </>
            )}
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Total</dt>
              <dd className="tabular-nums">{fmtKg(data.kgLiquido)}</dd>
            </div>
          </dl>

          {/* ── Procedência × classificação ──
              É como o cliente confere a carga na chegada: quem trouxe o quê, em
              kg. SEM VALOR NENHUM (D4) — o que o corretor pagou a cada produtor
              e por quanto revende não são assunto desta tela. */}
          <section className="space-y-2">
            <h2 className="text-xs uppercase tracking-wide text-muted-foreground">Por produtor</h2>
            <div className="overflow-x-auto -mx-4 px-4">
              <table className="w-full text-sm" data-testid="tabela-carga-publica">
                <thead>
                  <tr className="text-xs uppercase text-muted-foreground">
                    <th className="text-left font-medium py-1">Produtor</th>
                    {data.colunas.map(c => (
                      <th key={c.chave} className="text-right font-medium py-1 px-1.5 whitespace-nowrap">{c.rotulo}</th>
                    ))}
                    <th className="text-right font-medium py-1 whitespace-nowrap">Total kg</th>
                  </tr>
                </thead>
                <tbody>
                  {data.produtores.map((p, i) => (
                    <tr key={`${p.nome}-${i}`} className="border-t" data-testid={`produtor-publico-${i}`}>
                      <td className="py-1.5">
                        {p.nome}
                        {p.fazenda && <span className="block text-xs text-muted-foreground">{p.fazenda}</span>}
                      </td>
                      {data.colunas.map(c => (
                        <td key={c.chave} className="py-1.5 px-1.5 text-right tabular-nums">
                          {p.porClassificacao[c.chave] ? fmtKg(p.porClassificacao[c.chave]) : "—"}
                        </td>
                      ))}
                      <td className="py-1.5 text-right tabular-nums font-medium">{fmtKg(p.totalKg)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t font-semibold">
                    <td className="py-1.5">Total líquido</td>
                    {data.colunas.map(c => (
                      <td key={c.chave} className="py-1.5 px-1.5 text-right tabular-nums">
                        {data.totaisPorClassificacao[c.chave] ? fmtKg(data.totaisPorClassificacao[c.chave]) : "—"}
                      </td>
                    ))}
                    <td className="py-1.5 text-right tabular-nums">{fmtKg(data.kgLiquido)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {/* A diferença entre a balança e a soma dos romaneios é INFORMAÇÃO
              (quebra, umidade), não erro a esconder. `null` quando não há bruto
              ou quando o corretor desligou a exibição (D5). */}
          {data.kgBruto != null && (
            <p className="text-sm text-muted-foreground tabular-nums">
              Peso bruto {fmtKg(data.kgBruto)} · Líquido {fmtKg(data.kgLiquido)}
              {" · "}Diferença {fmtKg(data.kgBruto - data.kgLiquido)}
            </p>
          )}

          <Button variant="outline" className="w-full" asChild data-testid="button-baixar-pdf">
            <a href={`/api/publico/carga/${token}/pdf`}>
              <Download className="h-4 w-4 mr-1" />Baixar PDF
            </a>
          </Button>

          <div className="pt-2 border-t space-y-3">
            <p className="text-xs text-muted-foreground">
              Documento gerado por {data.comprador.nome} no Rizoma. Este link é pessoal.
            </p>
            {/* Sem esta linha, a ausência de valores é lida como documento
                incompleto — e alguém liga perguntando pelo total. */}
            <p className="text-xs text-muted-foreground">
              Valores comerciais não constam neste documento.
            </p>
            {/* O CTA do terceiro lado: o distribuidor cria conta e passa a ver
                todas as cargas que chegam para ele. O perfil já vem decidido pelo
                TIPO do link — quem chega por um link de carga é quem recebe. */}
            <Button
              className="w-full"
              onClick={() => setLocation(`/c/${token}/criar-acesso`)}
              data-testid="button-criar-acesso-destino"
            >
              Ver todas as minhas cargas
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
